<template>
  <div class="am3-input-container text-sm">
    <AM3InputLabel
      :label="label"
      :required="required"
      :input-id="inputId"
    />
    <div
      class="am3-input-wrapper flex flex-row items-center justify-between rounded-md"
      :class="[
        disabled ? 'disabled' : '',
        ($slots.right || $slots.left) ? 'focus-within:outline-brand hover:border-brand bg-surface-light px-3' : '',
        (disabled) ? disabledClasses : 'bg-surface-light hover:border-brand',
        (validationState === false || typeof validationState === 'string') ? 'error border border-solid border-[var(--font-colour-warning)]' : buttonBorderClasses,
      ]"
      :data-test-id="generateDataTestId(dataTestId, 'input-wrapper')"
    >
      <!-- Left slot -->
      <div
        v-if="$slots.left"
        class="am3-input-slot flex items-center justify-center"
      >
        <slot name="left" />
      </div>
      <!-- Input element -->
      <input
        :id="inputId"
        ref="inputRef"
        :class="($slots.right || $slots.left) ? '' : 'focus:outline-brand focus:outline-offset-0'"
        class="am3-input px-3 py-2.5 text-sm h-11 w-full border-none outline-none focus:ring-0 rounded-md"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :min="min"
        :max="max"
        type="number"
        step="any"
        :data-test-id="generateDataTestId(dataTestId, 'input')"
        @input="updateValue"
        @change="$emit('change', formatValue($event.target.value))"
        @keydown="handleKeyDown"
        @paste="handlePaste"
        @blur="handleBlur"
        @wheel.stop
      >

      <!-- Right slot -->
      <div
        v-if="$slots.right"
        class="am3-input-slot flex items-center justify-center"
      >
        <slot name="right" />
      </div>
    </div>

    <AM3InputError :error="validationState" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import '@/components/am3-generic/core/theme.css'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'
import AM3InputError from '@/components/am3-generic/inputs/AM3InputError.vue'
import AM3InputLabel from '@/components/am3-generic/inputs/AM3InputLabel.vue'

const props = defineProps({
  // v-model value
  modelValue: {
    type: [String, Number],
    default: '',
  },

  // Input label (string or false to hide)
  label: {
    type: [String, Boolean],
    default: '',
  },

  // Placeholder text
  placeholder: {
    type: String,
    default: props => 'Enter ' + props.label.toLowerCase(),
  },

  // Disabled state
  disabled: {
    type: Boolean,
    default: false,
  },

  // Required field
  required: {
    type: Boolean,
    default: false,
  },

  // Validation function
  validate: {
    type: Function,
    default: () => true,
  },

  // Input restriction function
  restrict: {
    type: Function,
    default: () => true,
  },

  // Data test id prefix
  dataTestId: {
    type: String,
    default: props => props.label,
  },

  // Number of decimal places
  decimals: {
    type: Number,
    default: 0,
  },

  // Minimum value
  min: {
    type: Number,
    default: undefined,
  },

  // Maximum value
  max: {
    type: Number,
    default: undefined,
  },

  // Allow negative values
  allowNegative: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'change', 'enter', 'blur'])

const buttonBorderClasses = 'rounded-md border border-1 border-surface-dark outline-1 outline-offset-0 outline-transparent outline-none'
const disabledClasses = 'text-disabled cursor-not-allowed hover:border-surface-dark hover:outline-none text-white'

const inputId = ref(`am3-input-${uuidv4()}`)
const inputRef = ref<HTMLInputElement | null>(null)

const {
  validationState,
  runValidation: _runValidation,
  resetValidation,
  handleBlur: _handleBlur,
} = useFormValidation(props.validate, props.modelValue, props.required)

const runValidation = () => {
  return _runValidation(props.modelValue, props.required)
}

const handleBlur = () => {
  _handleBlur(props.modelValue)
  validationState.value = props.validate(props.modelValue as string)
  emit('blur')
}

defineExpose({
  runValidation,
  resetValidation,
  handleBlur,
})

const formatValue = (value: string | number): number | null => {
  if (value === '' || value === null) return null

  let numValue = typeof value === 'string' ? parseFloat(value) : value

  if (!props.allowNegative && numValue < 0) numValue = 0

  if (props.decimals >= 0) {
    return parseFloat(numValue.toFixed(props.decimals))
  }

  return numValue
}

// Update model value
const updateValue = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value === '' ? null : formatValue(target.value)
  target.value = '' + value
  emit('update:modelValue', value)
}

// Handle keydown event with restrictions
const handleKeyDown = (event: KeyboardEvent) => {
  const input = event.target as HTMLInputElement
  const value = input.value
  const selectionStart = input.selectionStart || 0
  const selectionEnd = input.selectionEnd || 0

  // Prevent negative sign if not allowed
  if (!props.allowNegative && event.key === '-') {
    event.preventDefault()
    return
  }

  // Prevent decimal point if decimals is set to 0
  if (props.decimals === 0 && event.key === '.') {
    event.preventDefault()
    return
  }

  // Prevent typing a second decimal point
  if (event.key === '.' && value.includes('.')) {
    event.preventDefault()
    return
  }

  // Prevent additional decimal places beyond the allowed limit
  if (!isNaN(parseInt(event.key))) {
    // Check if we already have a decimal point
    const decimalIndex = value.indexOf('.')

    if (decimalIndex !== -1) {
      // If cursor is after the decimal point
      if (selectionStart > decimalIndex) {
        // Calculate how many decimal places we already have
        const decimalPlaces = value.length - decimalIndex - 1

        // If we're not replacing existing digits and already have max decimals
        if (selectionStart === selectionEnd && decimalPlaces >= props.decimals) {
          event.preventDefault()
          return
        }
      }
    }
  }

  // Apply custom restrictions if provided
  if (!props.restrict(event)) {
    event.preventDefault()
    return
  }

  if (event.key === 'Enter') {
    emit('enter', formatValue(value))
  }
}

// Handle paste event with restrictions
const handlePaste = (event: ClipboardEvent) => {
  if (!props.restrict(event)) {
    event.preventDefault()
  }
}
</script>
